import { useQuery } from '@tanstack/react-query';
import { Icon, type IconName } from '@uplink/ui';
import {
  type CSSProperties,
  type FormEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { errorText, useTRPC } from '../api.ts';
import logo from '../assets/uplink-logo.svg';
import { formatMoney, usePrefs } from '../prefs.tsx';
import { telegramMiniAppUrl } from '../telegram.ts';
import { QrCode } from '../vpn.tsx';
import { LANDING_TEXT } from './landing-i18n.tsx';

const usd = (cents: number) => formatMoney(cents, 'USD', null);
const ZONES = ['com', 'io', 'app', 'xyz', 'dev', 'net'];
const delay = (ms: number) => ({ '--d': `${ms}ms` }) as CSSProperties;

/** Элементы с `data-reveal` плавно появляются при прокрутке до них. */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>('[data-reveal]');
    if (!('IntersectionObserver' in window)) {
      for (const el of els) el.classList.add('is-in');
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px' },
    );
    for (const el of els) io.observe(el);
    return () => io.disconnect();
  }, []);
}

/** Макет кабинета выпрямляется при прокрутке, а навигация превращается в стеклянную плашку. */
function useScrollEffects(tilt: RefObject<HTMLDivElement | null>) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const el = tilt.current;
      if (!el || reduce) return;
      const rect = el.getBoundingClientRect();
      const progress = Math.min(
        1,
        Math.max(0, (window.innerHeight - rect.top) / (window.innerHeight * 0.85)),
      );
      el.style.setProperty('--tilt', `${(1 - progress) * 18}deg`);
      el.style.setProperty('--lift', `${(1 - progress) * 40}px`);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [tilt]);
  return scrolled;
}

/** Заголовок секции: иконка, градиентный заголовок и краткое описание. */
function SectionHead({ icon, title, lede }: { icon: IconName; title: string; lede: string }) {
  return (
    <header className="lp-head" data-reveal>
      <span className="lp-chip" aria-hidden>
        <Icon name={icon} size={22} />
      </span>
      <h2 className="lp-grad">{title}</h2>
      <p>{lede}</p>
    </header>
  );
}

function Bars() {
  return (
    <span className="lp-bars" aria-hidden>
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

/** Нарисованный на CSS кабинет Uplink с теми же сущностями, что видит пользователь. */
function ProductWindow({ t }: { t: (typeof LANDING_TEXT)['ru'] }) {
  return (
    <div className="lp-window" aria-hidden="true">
      <div className="lp-window__bar">
        <span />
        <span />
        <span />
        <em>uplinkcloud.io</em>
      </div>
      <div className="lp-window__body">
        <div className="lp-mside">
          <b>
            <img src={logo} alt="" width={22} height={22} /> Uplink
          </b>
          <span className="is-on">
            <Icon name="home" size={15} weight="fill" /> {t.dashboard.home}
          </span>
          <span>
            <Icon name="globe" size={15} /> {t.dashboard.domains}
          </span>
          <span>
            <Icon name="server" size={15} /> {t.dashboard.servers}
          </span>
          <span>
            <Icon name="wallet" size={15} /> {t.dashboard.balance}
          </span>
          <span>
            <Icon name="user" size={15} /> {t.dashboard.profile}
          </span>
        </div>
        <div className="lp-mmain">
          <div className="lp-mtop">
            <strong>{t.dashboard.greeting}</strong>
            <span className="lp-mpill">$64.20</span>
            <span className="lp-mbtn">{t.dashboard.topUp}</span>
          </div>
          <div className="lp-mgrid">
            <div className="lp-mcard lp-mbalance">
              <small>{t.dashboard.balance}</small>
              <b>$64.20</b>
              <small>{t.dashboard.monthly}</small>
            </div>
            <div className="lp-mcard lp-mchart">
              <small>{t.dashboard.cpu}</small>
              <svg viewBox="0 0 300 70" preserveAspectRatio="none" aria-hidden>
                <defs>
                  <linearGradient id="lp-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#7a86ff" stopOpacity="0.45" />
                    <stop offset="1" stopColor="#7a86ff" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0 52 L25 48 L50 50 L75 34 L100 38 L125 26 L150 32 L175 20 L200 28 L225 14 L250 22 L275 12 L300 18 L300 70 L0 70 Z"
                  fill="url(#lp-fill)"
                />
                <path
                  d="M0 52 L25 48 L50 50 L75 34 L100 38 L125 26 L150 32 L175 20 L200 28 L225 14 L250 22 L275 12 L300 18"
                  fill="none"
                  stroke="#7a86ff"
                  strokeWidth="2"
                />
              </svg>
            </div>
            <div className="lp-mcard lp-mvpn">
              <QrCode text="vless://uplink@203.0.113.24:443?security=reality#Uplink" />
              <div>
                <span className="lp-mok">{t.dashboard.vpnReady}</span>
                <b>VLESS Reality</b>
                <small>vpn-nl · {t.dashboard.amsterdam}</small>
              </div>
            </div>
          </div>
          <div className="lp-mtable">
            <div>
              <b>web-prod</b>
              <span>{t.dashboard.frankfurt} · 2 vCPU · 4 GB</span>
              <code>203.0.113.24</code>
              <Bars />
            </div>
            <div>
              <b>vpn-nl</b>
              <span>{t.dashboard.amsterdam} · 1 vCPU · 1 GB</span>
              <code>198.51.100.7</code>
              <Bars />
            </div>
            <div>
              <b>
                <code>annabakes.com</code>
              </b>
              <span>{t.dashboard.renews}</span>
              <span>{t.dashboard.autoRenew}</span>
              <span className="lp-mdot" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Plan({
  icon,
  name,
  price,
  per,
  items,
  cta,
  badge,
  secondaryCta,
  from,
  onClick,
  onSecondaryClick,
  featured = false,
}: {
  icon: IconName;
  name: string;
  price: number | null | undefined;
  per: string;
  items: string[];
  cta: string;
  badge?: string;
  secondaryCta?: string;
  from?: string;
  onClick: () => void;
  onSecondaryClick?: () => void;
  featured?: boolean;
}) {
  return (
    <article className={`lp-plan${featured ? ' lp-plan--featured' : ''}`}>
      {featured ? <span className="lp-meteor" aria-hidden /> : null}
      <div className="lp-plan__name">
        <span className="lp-plan__icon">
          <Icon name={icon} size={20} />
        </span>
        <h3>{name}</h3>
        {badge ? <span className="lp-plan__badge">{badge}</span> : null}
      </div>
      <p className="lp-plan__price">
        {from ? <small>{from}</small> : null}
        <b>{price ? usd(price) : '—'}</b>
        <small>/ {per}</small>
      </p>
      <ul>
        {items.map((it) => (
          <li key={it}>
            <Icon name="check" size={16} />
            {it}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className={`lp-btn ${featured ? 'lp-btn--primary' : 'lp-btn--ghost'}`}
        onClick={onClick}
      >
        {cta}
      </button>
      {secondaryCta && onSecondaryClick ? (
        <button type="button" className="lp-plan__secondary" onClick={onSecondaryClick}>
          {secondaryCta}
        </button>
      ) : null}
    </article>
  );
}

function VpnPlan({
  title,
  shared,
  own,
  per,
  from,
  sharedPrice,
  ownPrice,
  onSharedClick,
  onOwnClick,
}: {
  title: string;
  shared: { name: string; items: string[]; cta: string };
  own: { name: string; items: string[]; cta: string; badge: string };
  per: string;
  from: string;
  sharedPrice: number;
  ownPrice: number | null | undefined;
  onSharedClick: () => void;
  onOwnClick: () => void;
}) {
  return (
    <article className="lp-plan lp-plan--vpn">
      <div className="lp-plan__name">
        <span className="lp-plan__icon">
          <Icon name="shield" size={20} />
        </span>
        <h3>{title}</h3>
      </div>
      <div className="lp-vpn-options">
        <div className="lp-vpn-option lp-vpn-option--featured">
          <div className="lp-vpn-option__title">
            <Icon name="key" size={18} />
            <strong>{shared.name}</strong>
          </div>
          <p className="lp-vpn-option__price">
            <b>{usd(sharedPrice)}</b>
            <small>/ {per}</small>
          </p>
          <p>{shared.items[0]}</p>
          <button type="button" className="lp-btn lp-btn--primary" onClick={onSharedClick}>
            {shared.cta}
          </button>
        </div>
        <div className="lp-vpn-option">
          <div className="lp-vpn-option__title">
            <Icon name="server" size={18} />
            <strong>{own.name}</strong>
            <span className="lp-plan__badge">{own.badge}</span>
          </div>
          <p className="lp-vpn-option__price">
            <small>{from}</small>
            <b>{ownPrice ? usd(ownPrice) : '—'}</b>
            <small>/ {per}</small>
          </p>
          <p>{own.items[0]}</p>
          <button type="button" className="lp-btn lp-btn--ghost" onClick={onOwnClick}>
            {own.cta}
          </button>
        </div>
      </div>
    </article>
  );
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="lp-faq__item">
      <summary>
        {q}
        <Icon name="chevronDown" size={18} />
      </summary>
      <p>{children}</p>
    </details>
  );
}

/** Главная страница сайта с поиском домена, обзором возможностей, ценами и началом работы. */
export function Landing({
  onSignIn,
  onSignUp,
  botUrl,
}: {
  onSignIn: () => void;
  onSignUp: (domain?: string) => void;
  botUrl: string | null;
}) {
  const { lang, setLang } = usePrefs();
  const t = LANDING_TEXT[lang];
  const trpc = useTRPC();
  const tilt = useRef<HTMLDivElement>(null);
  const scrolled = useScrollEffects(tilt);
  useReveal();
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const search = useQuery({
    ...trpc.domains.publicSearch.queryOptions({ query: submitted }),
    enabled: submitted.length > 0,
    staleTime: 60_000,
    retry: false,
  });
  const config = useQuery(trpc.config.queryOptions());
  const vpnStatus = useQuery(trpc.vpnRussiaStatus.queryOptions());
  const telegramUrl = botUrl ? telegramMiniAppUrl(botUrl) : null;
  const domainFrom = config.data?.domainFromCents;
  const serverFrom = config.data?.serverFromCents;
  const sharedVpn = config.data?.sharedVpn;
  const signUpForSharedVpn = () => {
    window.history.replaceState(null, '', '/?s=vpnSubscription');
    onSignUp();
  };
  const vpnCheckedHoursAgo = vpnStatus.data
    ? (Date.now() - Date.parse(vpnStatus.data.checkedAt)) / 3_600_000
    : Infinity;
  const showVpnStatus = !!vpnStatus.data?.works && vpnCheckedHoursAgo <= 48;
  const vpnCheckedInRussia = vpnStatus.data?.region?.toLocaleLowerCase('ru').includes('россия') ?? false;
  const vpnStatusPlace = vpnCheckedInRussia
    ? lang === 'ru'
      ? 'VPN работает в России'
      : 'VPN works in Russia'
    : lang === 'ru'
      ? 'Uplink VPN работает'
      : 'Uplink VPN works';
  const vpnStatusText =
    vpnCheckedHoursAgo < 24
      ? lang === 'ru'
        ? `${vpnStatusPlace} · Проверено сегодня`
        : `${vpnStatusPlace} · Checked today`
      : lang === 'ru'
        ? `${vpnStatusPlace} · Проверено вчера`
        : `${vpnStatusPlace} · Checked yesterday`;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim().toLowerCase();
    if (q) setSubmitted(q);
  };

  return (
    <div className={`lp${showVpnStatus ? ' lp--with-status' : ''}`}>
      <div className="lp-shafts" aria-hidden>
        <i />
        <i />
        <i />
      </div>

      {showVpnStatus ? (
        <a className="lp-vpn-status" href="/vpn-status">
          <span aria-hidden /> {vpnStatusText} <Icon name="chevron" size={15} />
        </a>
      ) : null}

      <nav className={`lp-nav${scrolled ? ' is-scrolled' : ''}`} aria-label={t.navLabel}>
        <div className="lp-nav__pill">
          <a className="lp-brand" href="/">
            <img src={logo} alt="" width={30} height={30} />
            Uplink
          </a>
          <div className="lp-nav__links">
            <a href="#product">{t.navProduct}</a>
            <a href="#pricing">{t.navPricing}</a>
            <a href="#faq">{t.navFaq}</a>
          </div>
          <div className="lp-nav__auth">
            <button
              type="button"
              className="lp-language"
              aria-label={t.languageLabel}
              title={t.languageLabel}
              onClick={() => setLang(lang === 'ru' ? 'en' : 'ru')}
            >
              {t.languageShort}
            </button>
            {telegramUrl ? (
              <a
                className="lp-btn lp-btn--telegram lp-nav__telegram"
                href={telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="bolt" size={17} />
                <span>{t.telegram}</span>
              </a>
            ) : null}
            <button type="button" className="lp-btn lp-btn--quiet" onClick={onSignIn}>
              {t.signIn}
            </button>
            <button type="button" className="lp-btn lp-btn--primary" onClick={() => onSignUp()}>
              {t.getStarted}
            </button>
          </div>
        </div>
      </nav>

      <section className="lp-hero">
        <h1 className="lp-grad" data-reveal>
          {t.heroTitle}
        </h1>
        <p className="lp-lede" data-reveal style={delay(80)}>
          {t.heroText}
        </p>

        {telegramUrl ? (
          <div className="lp-telegram-cta" data-reveal style={delay(120)}>
            <a
              className="lp-btn lp-btn--telegram lp-btn--lg"
              href={telegramUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="bolt" size={20} />
              {t.telegramCta}
            </a>
            <span>{t.telegramCtaHint}</span>
          </div>
        ) : null}

        <div className="lp-search-wrap" data-reveal style={delay(160)}>
          <form className="lp-search" onSubmit={submit} aria-label={t.domainSearch}>
            <Icon name="search" size={20} />
            <label htmlFor="land-q" className="ul-visually-hidden">
              {t.domainName}
            </label>
            <input
              id="land-q"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.domainPlaceholder}
              autoComplete="off"
              spellCheck={false}
            />
            <button type="submit" className="lp-btn lp-btn--primary">
              {t.search}
            </button>
          </form>

          {submitted ? (
            <div className="lp-results" aria-live="polite">
              {search.isPending ? (
                <div className="lp-result lp-result--muted">{t.checking}</div>
              ) : search.isError ? (
                <div className="lp-result lp-result--muted">{errorText(search.error)}</div>
              ) : search.data?.length ? (
                search.data.slice(0, 6).map((r) => (
                  <div key={r.domain} className="lp-result">
                    <span className="lp-domain">
                      {r.label}
                      <span>.{r.tld}</span>
                    </span>
                    {r.status === 'available' && r.price !== null ? (
                      <span className="lp-offer">
                        {r.renew !== null ? <small>{t.renews(usd(r.renew))}</small> : null}
                        <b>{usd(r.price)}</b>
                        <button
                          type="button"
                          className="lp-btn lp-btn--primary lp-btn--sm"
                          onClick={() => onSignUp(r.domain)}
                        >
                          {t.buy}
                        </button>
                      </span>
                    ) : (
                      <span className="lp-taken">{r.status === 'premium' ? t.premium : t.taken}</span>
                    )}
                  </div>
                ))
              ) : (
                <div className="lp-result lp-result--muted">{t.nothingFound}</div>
              )}
            </div>
          ) : (
            <div className="lp-zones">
              {ZONES.map((z) => (
                <button
                  key={z}
                  type="button"
                  onClick={() => {
                    const base = query.trim().toLowerCase().replace(/\..*$/, '') || 'yourbrand';
                    setQuery(`${base}.${z}`);
                    setSubmitted(`${base}.${z}`);
                  }}
                >
                  .{z}
                </button>
              ))}
              {domainFrom ? <span>{t.domainFrom(usd(domainFrom))}</span> : null}
            </div>
          )}
        </div>
      </section>

      <div className="lp-stage" id="product">
        <div className="lp-tilt" ref={tilt}>
          <span className="lp-meteor" aria-hidden />
          <ProductWindow t={t} />
        </div>
      </div>

      <section className="lp-facts" aria-label={t.factsLabel} data-reveal>
        {t.facts.map(([value, label]) => (
          <div key={label}>
            <b>{value}</b>
            <span>{label}</span>
          </div>
        ))}
      </section>

      <section className="lp-section">
        <SectionHead icon="bolt" title={t.productTitle} lede={t.productLede} />
        <div className="lp-bento">
          <article className="lp-card lp-card--wide" data-reveal>
            <div className="lp-vig lp-vig--search" aria-hidden>
              <div className="lp-vsearch">
                <Icon name="search" size={16} />
                <span className="lp-typing">kazancoffee</span>
              </div>
              <div className="lp-vrow">
                <code>
                  kazancoffee<span>.com</span>
                </code>
                <b>$13.09</b>
                <em>{t.available}</em>
              </div>
              <div className="lp-vrow">
                <code>
                  kazancoffee<span>.io</span>
                </code>
                <b>$42.09</b>
                <em>{t.available}</em>
              </div>
              <div className="lp-vrow">
                <code>
                  kazancoffee<span>.xyz</span>
                </code>
                <b>$3.39</b>
                <em>{t.available}</em>
              </div>
            </div>
            <h3>{t.domainsTitle}</h3>
            <p>{t.domainsText}</p>
          </article>

          <article className="lp-card" data-reveal style={delay(80)}>
            <div className="lp-vig lp-vig--qr" aria-hidden>
              <div className="lp-vqr">
                <QrCode text="vless://uplink@198.51.100.7:443?security=reality#Uplink" />
                <i className="lp-scan" />
              </div>
              <div className="lp-vchips">
                <span>VLESS</span>
                <span>AmneziaWG</span>
                <span>Outline</span>
                <span>WireGuard</span>
              </div>
            </div>
            <h3>{t.vpnTitle}</h3>
            <p>{t.vpnText}</p>
          </article>

          <article className="lp-card" data-reveal>
            <div className="lp-vig lp-vig--cities" aria-hidden>
              <div className="lp-vcities">
                {t.cityNames.map((c, index) => (
                  <div key={c}>
                    <i />
                    {c}
                    <small>{['12', '9', '84', '162', '21', '198'][index]} ms</small>
                  </div>
                ))}
              </div>
            </div>
            <h3>{t.citiesTitle}</h3>
            <p>{t.citiesText}</p>
          </article>

          <article className="lp-card" data-reveal style={delay(80)}>
            <div className="lp-vig lp-vig--chart" aria-hidden>
              <svg viewBox="0 0 300 110" preserveAspectRatio="none" aria-hidden="true">
                <path
                  className="lp-vline"
                  d="M0 80 C30 70 50 86 80 62 S130 70 160 48 S220 58 250 30 S290 36 300 26"
                  fill="none"
                  strokeWidth="2.5"
                />
              </svg>
              <span className="lp-vpill">CPU 18% · RAM 41%</span>
            </div>
            <h3>{t.monitoringTitle}</h3>
            <p>{t.monitoringText}</p>
          </article>

          <article className="lp-card" data-reveal style={delay(160)}>
            <div className="lp-vig lp-vig--chat" aria-hidden>
              <div className="lp-bubble">
                <b>{t.serverReady}</b>
                <span>{t.serverPassword}</span>
                <em>{t.openServer}</em>
              </div>
            </div>
            <h3>{t.telegramTitle}</h3>
            <p>{t.telegramText}</p>
          </article>
        </div>
      </section>

      <section className="lp-section lp-vpn-benefits">
        <SectionHead icon="shield" title={t.vpnBenefitsTitle} lede={t.vpnBenefitsLede} />
        <div className="lp-benefits">
          {t.vpnBenefits.map(([title, text], index) => (
            <article key={title} data-reveal style={index ? delay((index % 3) * 80) : undefined}>
              <span aria-hidden>
                <Icon
                  name={(['key', 'shield', 'bolt', 'users', 'settings', 'server'] as const)[index]!}
                  size={22}
                />
              </span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
        <div className="lp-trial-callout" data-reveal>
          <span className="lp-trial-callout__icon" aria-hidden>
            <Icon name="shield" size={26} />
          </span>
          <div>
            <h3>{t.trialTitle}</h3>
            <p>{t.trialText}</p>
          </div>
          <a className="lp-btn lp-btn--primary" href="/?trial=1">
            {t.trialAction}
            <Icon name="chevron" size={17} />
          </a>
        </div>
      </section>

      <section className="lp-section">
        <SectionHead icon="rocket" title={t.stepsTitle} lede={t.stepsLede} />
        <ol className="lp-steps">
          {t.steps.map(([title, text], index) => (
            <li key={title} data-reveal style={index ? delay(index * 80) : undefined}>
              <span>{index + 1}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="lp-section" id="pricing">
        <SectionHead icon="wallet" title={t.pricingTitle} lede={t.pricingLede} />
        <div className="lp-plans" data-reveal>
          <Plan
            icon="globe"
            name={t.plans.domains.name}
            price={domainFrom}
            per={t.perYear}
            from={t.from}
            items={t.plans.domains.items}
            cta={t.plans.domains.cta}
            onClick={() => onSignUp()}
          />
          <Plan
            icon="server"
            name={t.plans.servers.name}
            price={serverFrom}
            per={t.perMonth}
            from={t.from}
            items={t.plans.servers.items}
            cta={t.plans.servers.cta}
            onClick={() => onSignUp()}
          />
          {sharedVpn?.available ? (
            <VpnPlan
              title={t.plans.vpnGroupName}
              shared={t.plans.sharedVpn}
              own={t.plans.vpn}
              per={t.perMonth}
              from={t.from}
              sharedPrice={sharedVpn.priceMonthly}
              ownPrice={serverFrom}
              onSharedClick={signUpForSharedVpn}
              onOwnClick={() => window.location.assign('/?trial=1')}
            />
          ) : (
            <Plan
              icon="shield"
              name={t.plans.vpn.name}
              badge={t.plans.vpn.badge}
              price={serverFrom}
              per={t.perMonth}
              from={t.from}
              items={t.plans.vpn.items}
              cta={t.plans.vpn.cta}
              secondaryCta={t.plans.vpn.secondaryCta}
              onClick={() => window.location.assign('/?trial=1')}
              onSecondaryClick={() => onSignUp()}
            />
          )}
        </div>
      </section>

      <section className="lp-section lp-faq" id="faq">
        <h2 className="lp-grad" data-reveal>
          {t.questions}
        </h2>
        <div className="lp-faq__list" data-reveal>
          {(t.faq as [string, string][]).map(([question, answer]) => (
            <Faq key={question} q={question}>
              {answer}
            </Faq>
          ))}
        </div>
      </section>

      <section className="lp-cta" data-reveal>
        <div className="lp-cta__mark">
          <img src={logo} alt="" width={84} height={84} />
        </div>
        <h2 className="lp-grad">{t.ctaTitle}</h2>
        <p>{t.ctaText}</p>
        <div className="lp-cta__actions">
          <button type="button" className="lp-btn lp-btn--primary lp-btn--lg" onClick={() => onSignUp()}>
            {t.createAccount}
            <Icon name="chevron" size={18} />
          </button>
          {telegramUrl ? (
            <a
              className="lp-btn lp-btn--ghost lp-btn--lg"
              href={telegramUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.openTelegram}
            </a>
          ) : null}
        </div>
      </section>

      <footer className="lp-footer">
        <a className="lp-brand" href="/">
          <img src={logo} alt="" width={26} height={26} />
          Uplink
        </a>
        <span>{t.footer}</span>
        <span>© {new Date().getFullYear()} Uplink</span>
      </footer>
    </div>
  );
}
